/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import EnforceIcon from "@/src/assets/icons/contract/toggleable/IconEnforce";
import DefendIcon from "@/src/assets/icons/contract/toggleable/IconDefend";
import AdvisoryIcon from "@/src/assets/icons/contract/toggleable/IconQuestionmarkBubble";
import Person from "@/src/components/Form/Common/Person/Person";
import _includes from "lodash/includes";
import Address from "@/src/components/Form/Common/Address/Address";
import PersonIcon from "@/src/assets/icons/provision/toggleable/IconPerson";
import FamilyIcon from "@/src/assets/icons/generic/toggleable/IconFamilyDetailed";
import ContractBaseEnforceClaim from "./ContractBaseEnforceClaim";
import ContractBaseDefendClaim from "./ContractBaseDefendClaim";
import { dateFieldDefault, isPhoneNumber, minStringLength, pastOrSameDateDefault, requiredValidator } from "@/src/services/validationRules";
import { useForm } from "@/src/provider/Form/FormProvider";
import Group from "@/src/components/Wizard/Group";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import ValidatorDateField from "@/src/components/FormFields/ValidatorDateField";
import AdditionalClientsForm from "@/src/components/Form/Common/AdditionalClientsForm/AdditionalClientsForm";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase, CaseCategorySelectionMode } from "@/src/types/Entities/AbstractCase";
import { translate } from "@/src/services/translations/translations";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";

type RentalContractBaseProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  hideCaseCategories?: boolean;
  alternativeHeadline?: string;
};
export default function RentalContractBase({ submitProps, product, hideCaseCategories = false, alternativeHeadline }: RentalContractBaseProps) {
  const { values, handleSubmit, handleChange } = useForm();
  const shouldHideCaseCategories = hideCaseCategories || product.caseCategorySelectionMode === CaseCategorySelectionMode.FIXED;

  useEffect(() => {
    if (values.legalRepresentativeType === "me") {
      handleChange({
        target: {
          name: "clientLegalRepresentative.gender",
          value: values.customer.person.gender,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.givenName",
          value: values.customer.person.givenName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.familyName",
          value: values.customer.person.familyName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.streetAddress",
          value: values.customer.person.residenceAddress.streetAddress,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.postalCode",
          value: values.customer.person.residenceAddress.postalCode,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.addressLocality",
          value: values.customer.person.residenceAddress.addressLocality,
          type: "text",
        },
      });
      return;
    }
    handleChange({
      target: {
        name: "clientLegalRepresentative.gender",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.givenName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.familyName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.streetAddress",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.postalCode",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.addressLocality",
        value: "",
        type: "text",
      },
    });
  }, [values.legalRepresentativeType]);

  useEffect(() => {
    if (values.affectedPartyType === "coinsured") {
      handleChange({
        target: {
          name: "client.gender",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.givenName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.familyName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.birthDate",
          value: null,
        },
      });
    }
  }, [values.affectedPartyType]);

  const getCaseCategoryOptions = () => {
    let options = [
      {
        labelText: translate("rentalContract.caseCategory.options.enforceClaim.label", values),
        labelIcon: <EnforceIcon />,
        labelIconActive: <EnforceIcon active />,
        value: "enforceClaim",
        optionHintText: translate("rentalContract.caseCategory.options.enforceClaim.optionHintText", values),
      },
      {
        labelText: translate("rentalContract.caseCategory.options.defendClaim.label", values),
        labelIcon: <DefendIcon />,
        labelIconActive: <DefendIcon active />,
        value: "defendClaim",
        optionHintText: translate("rentalContract.caseCategory.options.defendClaim.optionHintText", values),
      },
    ];
    if (product.categoryChange === null || product.categoryChange.previousCaseCategory !== "advisory") {
      options = [
        {
          labelText: translate("rentalContract.caseCategory.options.advisory.label", values),
          labelIcon: <AdvisoryIcon />,
          labelIconActive: <AdvisoryIcon active />,
          value: "advisory",
          optionHintText: translate("rentalContract.caseCategory.options.advisory.optionHintText", values),
        },
        ...options,
      ];
    }
    return options;
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ihr Vertrag
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>{alternativeHeadline || "Allgemeines zu Ihrem Mietvertrag"}</strong>
              </FormLegend>
            </Grid>
            {!shouldHideCaseCategories && (
              <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                <Group
                  type="radio"
                  name="caseCategory"
                  question={translate("rentalContract.caseCategory.question", values)}
                  options={getCaseCategoryOptions()}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            )}
            <Person path="customer.person" headline={"Wie heißen Sie?"} />
            {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
              <Address path="customer.person.residenceAddress" headline={"Wie lautet Ihre aktuelle Anschrift? "} />
            )}
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>Wie können wir Sie erreichen?</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                name="customer.telephone"
                label="Ihre Telefonnummer"
                autoComplete="tel"
                validators={[
                  requiredValidator("Bitte geben Sie Ihre Telefonnummer an"),
                  {
                    validator: minStringLength,
                    params: [7],
                    message: "Ihre Telefonnummer ist zu kurz",
                  },
                  { validator: isPhoneNumber, message: "Ihre Telefonnummer enthält ungültige Zeichen" },
                ]}
              />
            </Grid>
            {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
              <>
                <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
                  <Group
                    type="radio"
                    name="contractWasTakenOverByOpposingParty"
                    question={translate(`rentalContract.contractWasTakenOverByOpposingParty.question`, values)}
                    options={[
                      {
                        labelText: translate(`rentalContract.contractWasTakenOverByOpposingParty.options.yes.label`, values),
                        labelIcon: <CheckIcon />,
                        labelIconActive: <CheckIcon active />,
                        value: "yes",
                      },
                      {
                        labelText: translate(`rentalContract.contractWasTakenOverByOpposingParty.options.no.label`, values),
                        labelIcon: <CrossIcon />,
                        labelIconActive: <CrossIcon active />,
                        value: "no",
                      },
                    ]}
                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                  />
                </Grid>
                <Grid size={12}>
                  <Group
                    type="radio"
                    name="affectedPartyType"
                    question={translate("rentalContract.affectedPartyType.question", values)}
                    options={[
                      {
                        labelText: translate("rentalContract.affectedPartyType.options.customer.label", values),
                        labelIcon: <PersonIcon />,
                        labelIconActive: <PersonIcon active />,
                        value: "customer",
                      },
                      {
                        labelText: translate("rentalContract.affectedPartyType.options.coinsured.label", values),
                        labelIcon: <FamilyIcon />,
                        labelIconActive: <FamilyIcon active />,
                        value: "coinsured",
                      },
                    ]}
                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                  />
                </Grid>
                {values.affectedPartyType === "customer" && (
                  <>
                    <AdditionalClientsForm allowThreeOrMoreClients={true} product={product} />
                  </>
                )}
                {values.affectedPartyType === "coinsured" && (
                  <>
                    <Person path="client" headline="Wie heißt die Person, die den Vertrag geschlossen hat?" />
                    <Address path="client.residenceAddress" headline={"Wo wohnt diese Person?"} />
                  </>
                )}
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>
                    {values.contractPartyType === "tenant"
                      ? "Wann wurde der Mietvertrag unterzeichnet? Prüfen Sie hierzu bitte Ihren Mietvertrag. WICHTIG: Es geht hier nicht um den Beginn des Mietverhältnisses, sondern um das Datum, an dem Sie den Vertrag unterzeichnet haben."
                      : "Wann haben beide Parteien den Mietvertrag unterzeichnet? WICHTIG: Es geht hier nicht um den Beginn des Mietverhältnisses, sondern um das Unterschriftsdatum. Wenn die Unterschriften zu verschiedenen Daten vorgenommen wurden, tragen Sie hier bitte das zeitlich spätere Datum ein."}
                  </FormLegend>
                </Grid>
                {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
                  <Grid size={12}>
                    <ValidatorDateField
                      name="contractDate"
                      label={translate("rentalContract.contractDate.label", values)}
                      validators={[requiredValidator("Bitte geben Sie das Vertragsdatum an"), dateFieldDefault, pastOrSameDateDefault]}
                      helperText={translate("rentalContract.contractDate.helperText", values)}
                    />
                  </Grid>
                )}
              </>
            )}
            {values.caseCategory === "enforceClaim" && <ContractBaseEnforceClaim />}
            {values.caseCategory === "defendClaim" && <ContractBaseDefendClaim product={product} />}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
